import { useDarkMode } from '../context/DarkModeContext'
import { usePlatform } from '../context/PlatformContext'

/**
 * useDarkClasses
 *
 * Returns named Tailwind class strings for common dark/light mode surface
 * patterns. When AfterTide is active, returns AfterTide's dark navy theme
 * regardless of the darkMode toggle.
 */
export const useDarkClasses = () => {
  const { darkMode } = useDarkMode()
  const { isAfterTide } = usePlatform()

  // AfterTide always uses its own dark theme
  if (isAfterTide) {
    return {
      page: 'bg-[#1a1a2e]',
      card: 'bg-[#16213e] border-[#16213e]',
      cardSubtle: 'bg-[#16213e] border-[#e2b04a]/10',
      cardElevated: 'bg-[#1a1a2e] border-[#e2b04a]/10',
      surface: 'bg-[#16213e]',
      surfaceMuted: 'bg-[#16213e]/50',
      border: 'border-[#16213e]',
      borderSubtle: 'border-[#e2b04a]/10',
      borderAccent: 'border-[#e2b04a]/20',
      borderAccentStrong: 'border-[#e2b04a]/30',
      divider: 'border-[#16213e]',
      textPrimary: 'text-[#f5f0e8]',
      textSecondary: 'text-[#a0a0b0]',
      textMuted: 'text-[#a0a0b0]',
      textFaint: 'text-[#a0a0b0]/60',
      textHeading: 'text-[#f5f0e8]',
      input:
        'bg-[#16213e] border-[#e2b04a]/30 text-[#f5f0e8] placeholder-[#a0a0b0]',
      inputOnCard:
        'bg-[#1a1a2e] border-[#e2b04a]/20 text-[#f5f0e8] placeholder-[#a0a0b0]',
      nav: 'bg-[#1a1a2e] border-[#16213e]',
      dropdown: 'bg-[#1a1a2e] border-[#16213e]',
      dropdownItemHover: 'hover:bg-[#16213e]',
      badgeNeutral: 'bg-[#16213e] text-[#a0a0b0]',
      isDark: true,
    }
  }

  const d = darkMode

  return {
    // ─── Page / Screen ───────────────────────────────────────────────────────
    /** Full-page background */
    page: d ? 'bg-gray-900' : 'bg-white',

    // ─── Surfaces ────────────────────────────────────────────────────────────
    /** Standard card / panel surface */
    card: d ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200',
    /** Card with lighter border (used in some Trove sections) */
    cardSubtle: d
      ? 'bg-gray-800 border-gray-700'
      : 'bg-gray-50 border-gray-100',
    /** Slightly elevated surface (nested cards) */
    cardElevated: d
      ? 'bg-gray-700 border-gray-600'
      : 'bg-gray-100 border-gray-200',
    /** Muted / secondary surface */
    surface: d ? 'bg-gray-900' : 'bg-white',
    /** Very subtle tinted surface */
    surfaceMuted: d ? 'bg-gray-700/50' : 'bg-gray-50',

    // ─── Borders ─────────────────────────────────────────────────────────────
    /** Standard divider / border */
    border: d ? 'border-gray-700' : 'border-gray-200',
    /** Subtle divider */
    borderSubtle: d ? 'border-gray-700' : 'border-gray-100',
    /** Accent-tinted border (used on inputs, interactive cards) */
    borderAccent: d ? 'border-gray-600' : 'border-gray-300',
    /** Stronger accent-tinted border */
    borderAccentStrong: d ? 'border-gray-500' : 'border-gray-400',
    /** Divider line (for <hr> or border-b) */
    divider: d ? 'border-gray-700' : 'border-gray-200',

    // ─── Text ────────────────────────────────────────────────────────────────
    /** Primary heading / body text */
    textPrimary: d ? 'text-white' : 'text-gray-900',
    /** Secondary / supporting text */
    textSecondary: d ? 'text-gray-300' : 'text-gray-600',
    /** Muted / placeholder text */
    textMuted: d ? 'text-gray-400' : 'text-gray-500',
    /** Very muted text. gray-500 fell below readable contrast on gray-800
     * cards in dark mode; gray-400 keeps it clearly subordinate but legible. */
    textFaint: d ? 'text-gray-400' : 'text-gray-400',
    /** Heading text (slightly different from primary in some contexts) */
    textHeading: d ? 'text-gray-100' : 'text-gray-900',

    // ─── Form Inputs ─────────────────────────────────────────────────────────
    /** Input field background + border + text */
    // Dark placeholders raised to gray-400 and borders to gray-600: at
    // gray-500/gray-700 both nearly vanished against the gray-900 field.
    input: d
      ? 'bg-gray-900 border-gray-600 text-gray-100 placeholder-gray-400'
      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400',
    /** Input field on a card surface */
    inputOnCard: d
      ? 'bg-gray-900 border-gray-600 text-gray-100 placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',

    // ─── Navigation ──────────────────────────────────────────────────────────
    /** Header / nav bar */
    nav: d ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
    /** Dropdown / popover */
    dropdown: d ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
    /** Dropdown item hover */
    dropdownItemHover: d ? 'hover:bg-gray-700' : 'hover:bg-gray-50',

    // ─── Badges / Tags ───────────────────────────────────────────────────────
    /** Neutral badge */
    badgeNeutral: d ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600',

    // ─── Utility ─────────────────────────────────────────────────────────────
    /** Raw boolean for cases where a ternary is unavoidable */
    isDark: d,
  }
}

export default useDarkClasses
